import React, { ReactNode } from "react"
import {
    ButtonGroup,
    Button,
    IconComputer,
    Panel,
    IconCLI,
    IconPaste,
    IconJSON
} from "dash-styled"
import { ReactComponent as TableIcon } from "icons/lib/table.svg"

export type InputMode = "input" | "csv" | "json" | "sdk"
export type ButtonVariant = "upload" | "simple" | "complex"

interface Props {
    testId?: string
    active?: InputMode
    onClick: (button: InputMode) => void
    children: ReactNode
    variant: ButtonVariant
    noSDKMode: boolean
    noPanel?: boolean
    single: boolean
    allowCSV?: boolean
}

export const Container: React.FC<Props> = ({
    active,
    children,
    onClick,
    variant,
    testId,
    noSDKMode,
    noPanel = false,
    single,
    allowCSV = false
}: Props) => {
    const sdk = noSDKMode
        ? []
        : [
              {
                  key: "sdk",
                  icon: IconCLI,
                  active: active === "sdk",
                  testId: "add-data-btn-sdk",
                  onClick: () => onClick("sdk"),
                  text: (
                      <>
                          Via command
                          <br />
                          line or Python
                      </>
                  )
              }
          ]
    const buttons = () => {
        switch (variant) {
            case "upload":
                return [
                    {
                        key: "input",
                        icon: IconComputer,
                        active: active === "input",
                        testId: "add-data-btn-input",
                        onClick: () => onClick("input"),
                        text: (
                            <>
                                From
                                <br />
                                computer
                            </>
                        )
                    },
                    ...(allowCSV
                        ? [
                              {
                                  key: "csv",
                                  icon: TableIcon,
                                  active: active === "csv",
                                  testId: "add-data-btn-csv",
                                  onClick: () => onClick("csv"),
                                  text: (
                                      <>
                                          CSV
                                          <br />
                                          of URL{!single && "s"}
                                      </>
                                  )
                              }
                          ]
                        : []),
                    {
                        key: "json",
                        icon: IconJSON,
                        active: active === "json",
                        testId: "add-data-btn-json",
                        onClick: () => onClick("json"),
                        text: (
                            <>
                                JSON
                                <br />
                                of URL{!single && "s"}
                            </>
                        )
                    },
                    ...sdk
                ]
            case "simple":
                return [
                    {
                        key: "input",
                        icon: IconPaste,
                        active: active === "input",
                        testId: "add-data-btn-input",
                        onClick: () => onClick("input"),
                        text: (
                            <>
                                Paste
                                <br />
                                directly
                            </>
                        )
                    },
                    ...(allowCSV
                        ? [
                              {
                                  key: "csv",
                                  icon: TableIcon,
                                  active: active === "csv",
                                  testId: "add-data-btn-csv",
                                  onClick: () => onClick("csv"),
                                  text: (
                                      <>
                                          via
                                          <br />
                                          CSV
                                      </>
                                  )
                              }
                          ]
                        : []),
                    {
                        key: "json",
                        icon: IconJSON,
                        active: active === "json",
                        testId: "add-data-btn-json",
                        onClick: () => onClick("json"),
                        text: (
                            <>
                                via
                                <br />
                                JSON
                            </>
                        )
                    },
                    ...sdk
                ]
            case "complex":
                return [
                    {
                        key: "json",
                        icon: IconJSON,
                        active: active === "json",
                        testId: "add-data-btn-json",
                        onClick: () => onClick("json"),
                        text: (
                            <>
                                via
                                <br />
                                JSON
                            </>
                        )
                    },
                    ...sdk
                ]
        }
    }

    return (
        <div data-testid={testId ? `${testId}-container` : ""}>
            <div className="flex justify-center mb-8">
                <div className="max-w-lg w-full">
                    <ButtonGroup equalWidth={false}>
                        {buttons().map((btn, i) => (
                            <Button
                                active={btn.active}
                                color="white"
                                key={btn.key}
                                onClick={btn.active ? undefined : btn.onClick}
                                style={{
                                    boxShadow: "none",
                                    maxWidth: "140px",
                                    minWidth: "140px",
                                    color: "#000"
                                }}
                                testId={btn.testId}
                            >
                                <div className="flex flex-col justify-center items-center">
                                    {React.createElement(btn.icon, {
                                        className: "text-rhino-750 w-10 h-10"
                                    })}
                                    <span className="mt-2 text-sm">
                                        {btn.text}
                                    </span>
                                </div>
                            </Button>
                        ))}
                    </ButtonGroup>
                </div>
            </div>
            {noPanel ? (
                children
            ) : (
                <Panel
                    noPadding={true}
                    className="max-w-4xl mx-auto overflow-hidden"
                >
                    {children}
                </Panel>
            )}
        </div>
    )
}
